import { createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Award, BookMarked, FileText, Flame, Lock, NotebookPen, Sparkles } from "@fleet/icons";
import {
  type BlockInput,
  computeApuntePlayerState,
  levelProgress,
  type PageInput,
} from "@apunte-digital/shared/gamification";
import { useAllBlocksQuery, usePagesQuery } from "@apunte-digital/shared/solid";
import { Card, CardContent } from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

const WEEKLY_QUEST_TARGET = 3;

const BADGE_META: Record<string, { label: ReturnType<typeof msg>; icon: typeof FileText }> = {
  "first-note": { label: msg`First note`, icon: NotebookPen },
  "pages-10": { label: msg`Ten notes`, icon: FileText },
  "pages-50": { label: msg`Full notebook`, icon: BookMarked },
  "blocks-100": { label: msg`100 blocks`, icon: FileText },
  "blocks-500": { label: msg`Prolific`, icon: Award },
  "notes-streak-3": { label: msg`3 days of notes`, icon: Flame },
  "notes-streak-7": { label: msg`Productive week`, icon: Flame },
  "notes-streak-30": { label: msg`Productive month`, icon: Flame },
  "level-5": { label: msg`Level 5`, icon: Sparkles },
  "level-10": { label: msg`Level 10`, icon: Sparkles },
};

const BADGE_ORDER = Object.keys(BADGE_META);

function Bar(props: { value: number }) {
  return (
    <div class="h-2 overflow-hidden rounded-full bg-muted/40" aria-hidden="true">
      <div
        class="h-full rounded-full bg-accent transition-[width] duration-500 ease-out motion-reduce:transition-none"
        style={{
          width: `${Math.round(Math.max(0, Math.min(1, props.value)) * 100)}%`,
        }}
      />
    </div>
  );
}

/**
 * Gamification panel for Apunte Digital (soft productivity): level + XP,
 * activity streak, weekly quest, and badges (notes/blocks). State
 * is recomputed on the client from already-synchronized pages + blocks (local-first).
 */
export function GamificationPanel() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const pages = usePagesQuery({
    db: () => db(),
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });
  const blocks = useAllBlocksQuery({
    db: () => db(),
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const player = createMemo(() => {
    const pageInputs: PageInput[] = (pages.data ?? []).map((p) => ({
      createdAt: p.created_at,
    }));
    const blockInputs: BlockInput[] = (blocks.data ?? []).map((b) => ({
      updatedAt: b.updated_at,
    }));
    return computeApuntePlayerState({ pages: pageInputs, blocks: blockInputs });
  });

  const progress = createMemo(() => levelProgress(player().xp));
  const streak = createMemo(() => player().streaks.notes?.current ?? 0);
  const best = createMemo(() => player().streaks.notes?.best ?? 0);
  const owned = createMemo(() => new Set(player().badges));
  const weekly = createMemo(() =>
    Math.min(WEEKLY_QUEST_TARGET, player().quests["weekly-notes"]?.progress["page.created"] ?? 0),
  );

  return (
    <Card data-testid="gamification-panel">
      <CardContent class="p-4">
        <div class="flex items-center gap-3">
          <div class="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Sparkles class="size-5" aria-hidden="true" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-baseline justify-between gap-2">
              <span class="font-semibold" data-testid="gami-level">
                {t(msg`Level {level}`, { level: progress().level })}
              </span>
              <span class="text-xs text-muted-foreground" data-testid="gami-xp">
                {t(msg`{xp} XP`, { xp: player().xp })}
              </span>
            </div>
            <div class="mt-2">
              <Bar value={progress().ratio} />
            </div>
            <span class="mt-1 block text-xs text-muted-foreground">
              {t(msg`{xp} XP to next level`, { xp: progress().xpToNext })}
            </span>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-2 gap-3">
          <div class="rounded-xl border border-border/40 p-3 text-center">
            <Flame class="mx-auto size-5 text-orange-500" aria-hidden="true" />
            <div class="mt-1 text-lg font-bold tabular-nums" data-testid="gami-streak">
              {streak()}
            </div>
            <div class="text-xs text-muted-foreground">{t(msg`Activity streak`)}</div>
          </div>
          <div class="rounded-xl border border-border/40 p-3 text-center">
            <Award class="mx-auto size-5 text-amber-500" aria-hidden="true" />
            <div class="mt-1 text-lg font-bold tabular-nums">{best()}</div>
            <div class="text-xs text-muted-foreground">{t(msg`Best streak`)}</div>
          </div>
        </div>

        <div class="mt-4">
          <div class="flex items-center justify-between text-xs">
            <span class="font-medium">{t(msg`Weekly quest`)}</span>
            <span class="text-muted-foreground tabular-nums">
              {weekly()}/{WEEKLY_QUEST_TARGET}
            </span>
          </div>
          <div class="mt-1.5">
            <Bar value={weekly() / WEEKLY_QUEST_TARGET} />
          </div>
        </div>

        <div class="mt-4">
          <div class="mb-2 text-xs font-medium">
            {t(msg`Badges`)}{" "}
            <span class="text-muted-foreground">
              ({owned().size}/{BADGE_ORDER.length})
            </span>
          </div>
          <div class="grid grid-cols-3 gap-2 sm:grid-cols-5">
            <For each={BADGE_ORDER}>
              {(id) => {
                const meta = BADGE_META[id]!;
                const Icon = meta.icon;
                const has = () => owned().has(id);
                return (
                  <div
                    class="flex flex-col items-center gap-1 rounded-xl border border-border/40 p-2 text-center transition-opacity motion-reduce:transition-none"
                    classList={{ "opacity-40": !has() }}
                    title={t(meta.label)}
                  >
                    <Show
                      when={has()}
                      fallback={<Lock class="size-5 text-muted-foreground" aria-hidden="true" />}
                    >
                      <Icon class="size-5 text-accent" aria-hidden="true" />
                    </Show>
                    <span class="line-clamp-2 text-[10px] leading-tight text-muted-foreground">
                      {t(meta.label)}
                    </span>
                  </div>
                );
              }}
            </For>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
